import { GlobalVars } from '../../global'
import { handleNetworkResponseAndGetJsonAsync, Network } from './Network'
import { EmailMessageDto, EmailTemplateDto, SurveyResponseBriefDto, SurveyResponseDto } from './sharedTypes/Email'
import { GetSettingsResponse } from './collections/emailAutomationSettings/getSettings/getSettings_200'
import { UpdateSettingsRequest } from './collections/emailAutomationSettings/updateSettings/updateSettings_put'
import { UpdateSettingsResponse } from './collections/emailAutomationSettings/updateSettings/updateSettings_200'

export class EmailManager {
    static _instance: EmailManager | null = null

    static getManager(): EmailManager {
        if (this._instance === null) {
            this._instance = new EmailManager()
        }
        return this._instance
    }

    async getEmailMessagesListAsync(): Promise<{ messages: EmailMessageDto[] }> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/message`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: { messages: EmailMessageDto[] } = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getFilteredEmailMessagesListAsync(dateFrom: string, dateTo: string): Promise<{ messages: EmailMessageDto[] }> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/message?CreatedFrom=${dateFrom}&CreatedTo=${dateTo}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: { messages: EmailMessageDto[] } = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async deleteEmailMessageAsync(id: string): Promise<void> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/message/${id}`
        try {
            response = await Network.fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: void = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateEmailMessageAsync(id: string, subject: string, emailBody: string): Promise<EmailMessageDto> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/message/${id}/update`
        const body = { subject: subject, body: emailBody }
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: EmailMessageDto = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async sendEmailMessageAsync(id: string): Promise<void> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/message/${id}/send`
        const body = id
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: body,
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: void = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getEmailTemplatesListAsync(): Promise<{ emailTemplates: EmailTemplateDto[] }> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/template`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: { emailTemplates: EmailTemplateDto[] } = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateEmailTemplateAsync(
        id: string,
        subject: string,
        emailBody: string,
        isEnabled: boolean
    ): Promise<EmailTemplateDto> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/template/${id}/update`
        const body = { subject: subject, bodyTemplate: emailBody, isEnabled: isEnabled }
        try {
            response = await Network.fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: EmailTemplateDto = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getSurveysListAsync(): Promise<{ surveyResponses: SurveyResponseBriefDto[] }> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/survey/responses`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: { surveyResponses: SurveyResponseBriefDto[] } = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getFilteredSurveysListAsync(dateFrom: string, dateTo: string): Promise<{ surveyResponses: SurveyResponseBriefDto[] }> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/survey/responses?EventsFrom=${dateFrom}&EventsTo=${dateTo}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: { surveyResponses: SurveyResponseBriefDto[] } = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getSurveyAsync(id: string, surveyType: string): Promise<SurveyResponseDto> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/survey/responses/${id}?surveyType=${surveyType}`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: SurveyResponseDto = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async getEmailAutomationSettingsAsync(): Promise<GetSettingsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/automation/settings`
        try {
            response = await Network.fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: GetSettingsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }

    async updateEmailAutomationSettingsAsync(body: UpdateSettingsRequest): Promise<UpdateSettingsResponse> {
        let response: Response | null = null
        const url = `${GlobalVars.SERVER_ENDPOINT}/email/automation/settings`
        try {
            response = await Network.fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            })
        } catch (e) {
            throw new Error('Error executing fetch request.')
        }
        if (response) {
            const result: UpdateSettingsResponse = await handleNetworkResponseAndGetJsonAsync(response)
            return result
        } else {
            throw new Error('No response from server returned.')
        }
    }
}
